import { useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { MapPin } from "lucide-react";
import { statusLabel, statusTone, fmtTimestamp } from "../../services/fleetx_service";
import "leaflet/dist/leaflet.css";
import "./FleetMap.css";

const TONE_COLOR = {
  positive: "#178A4C",
  neutral: "#B4780F",
  negative: "#C6303E",
  idle: "#64748B",
  default: "#64748B",
};

function markerIcon(tone, heading) {
  const color = TONE_COLOR[tone] || TONE_COLOR.default;
  const rotation = typeof heading === "number" ? heading : 0;
  const html = `
    <div style="width:30px;height:30px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
        <circle cx="15" cy="15" r="13" fill="${color}" fill-opacity="0.16" />
        <g transform="rotate(${rotation} 15 15)">
          <path d="M15 4 L23 24 L15 19 L7 24 Z" fill="${color}" stroke="#ffffff" stroke-width="1.5" />
        </g>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "fleetmap-marker-icon", iconSize: [30, 30], iconAnchor: [15, 15] });
}

function FitBounds({ points }) {
  const map = useMap();
  const prevCount = useRef(-1);
  useEffect(() => {
    if (points.length === 0 || prevCount.current === points.length) return;
    prevCount.current = points.length;
    const bounds = L.latLngBounds(points.map((p) => [p.latitude, p.longitude]));
    map.fitBounds(bounds, { padding: [60, 60] });
  }, [points, map]);
  return null;
}

export default function FleetMap({ vehicles, onSelectVehicle, loading }) {
  const located = useMemo(
    () => vehicles.filter((v) => typeof v.latitude === "number" && typeof v.longitude === "number"),
    [vehicles]
  );
  const unlocated = vehicles.length - located.length;

  return (
    <div className="fleetmap-wrap">
      {unlocated > 0 && (
        <div className="fleetmap-banner">
          <MapPin size={13} />
          {unlocated} vehicle{unlocated === 1 ? "" : "s"} without location data not shown
        </div>
      )}

      <div className={`fleetmap-canvas ${loading ? "fleetmap-canvas--loading" : ""}`}>
        <MapContainer className="fleetmap-gmap" center={[20.5937, 78.9629]} zoom={5} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={located} />

          {located.map((v) => (
            <Marker
              key={v.vehicleId}
              position={[v.latitude, v.longitude]}
              icon={markerIcon(statusTone(v.currentStatus), v.heading)}
            >
              <Popup>
                <div className="fleetmap-infowindow">
                  <div className="fleetmap-iw-header">
                    <span className="fleetmap-iw-badge">{v.vehicleNumber}</span>
                    <span className={`fleetmap-iw-pill fleetmap-iw-pill--${statusTone(v.currentStatus)}`}>
                      {statusLabel(v.currentStatus)}
                    </span>
                  </div>
                  {v.vehicleName && <div className="fleetmap-iw-name">{v.vehicleName}</div>}
                  <div className="fleetmap-iw-row">
                    <span>Speed</span>
                    <strong>{typeof v.speed === "number" ? `${Math.round(v.speed)} km/h` : "—"}</strong>
                  </div>
                  <div className="fleetmap-iw-row">
                    <span>Updated</span>
                    <strong>{fmtTimestamp(v.lastUpdatedAt)}</strong>
                  </div>
                  {v.address && <div className="fleetmap-iw-address">{v.address}</div>}
                  <button className="fleetmap-iw-detail-btn" onClick={() => onSelectVehicle(v.vehicleNumber)}>
                    View full details
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
